import { useMutation } from '@tanstack/react-query'
import { orderBy, query } from 'firebase/firestore'
import { parseAsString, useQueryState } from 'nuqs'
import { useEffect, useMemo, useState } from 'react'
import { useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'

import {
  deleteApiTablesMasterByIdMutation,
  patchApiTablesMasterByIdMutation,
  postApiTablesMasterImportMutation,
  postApiTablesMasterMutation,
} from '@/lib/api/client/@tanstack/react-query.gen'
import type { TableMasterResponse } from '@/lib/api/client/types.gen'
import { collectionRef } from '@/lib/firebase/firestore'
import { listenToCollection } from '@/lib/firebase/utils'

interface TableAssignment {
  id: string
  tableRef?: { id: string }
  masterGuestRef?: { id: string }
  seats?: number
}

interface ApiError {
  body?: {
    error?: string
  }
  message?: string
}

export const useTableList = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const [sortBy, setSortBy] = useQueryState(
    'sortBy',
    parseAsString.withDefault('tableNumber').withOptions({ shallow: true }),
  )
  const [sortOrder, setSortOrder] = useQueryState(
    'sortOrder',
    parseAsString.withDefault('asc').withOptions({ shallow: true }),
  )

  const [tables, setTables] = useState<TableMasterResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const pendingUpdates = useRef<Map<string, Partial<TableMasterResponse>>>(
    new Map(),
  )

  const [tableAssignments, setTableAssignments] = useState<TableAssignment[]>(
    [],
  )

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Real-time listener
  useEffect(() => {
    setIsLoading(true)

    // Listen for tables
    const qTables = query(
      collectionRef('tables_master'),
      orderBy('createdAt', 'asc'),
    )
    const unsubscribeTables = listenToCollection(
      qTables,
      (data) => {
        setTables(data as TableMasterResponse[])
        setIsLoading(false)
      },
      {
        map: (doc) => {
          const docData = doc.data() as Omit<TableMasterResponse, 'id'>
          const pending = pendingUpdates.current.get(doc.id)

          if (pending) {
            const isMatch = Object.entries(pending).every(
              ([key, value]) => docData[key as keyof typeof docData] === value,
            )
            if (isMatch) {
              pendingUpdates.current.delete(doc.id)
            }
          }

          return {
            id: doc.id,
            ...docData,
            ...pendingUpdates.current.get(doc.id),
          } as TableMasterResponse
        },
      },
    )

    // Listen for table assignments
    const unsubscribeAssignments = listenToCollection(
      collectionRef('table_assignments'),
      (data) => setTableAssignments(data as TableAssignment[]),
      {},
    )

    return () => {
      unsubscribeTables()
      unsubscribeAssignments()
    }
  }, [])

  const tableOccupancyMap = useMemo(() => {
    const map: Record<string, number> = {}
    tableAssignments.forEach((ta) => {
      const tableId = ta.tableRef?.id
      if (!tableId) return

      const count = ta.seats || 1
      map[tableId] = (map[tableId] || 0) + count
    })
    return map
  }, [tableAssignments])

  // Mutations
  const createMutation = useMutation({
    ...postApiTablesMasterMutation(),
    onSuccess: () => {
      toast.success('สร้างโต๊ะสำเร็จ')
    },
    onError: () => {
      toast.error('เกิดข้อผิดพลาดในการสร้างโต๊ะ')
    },
  })

  const updateMutation = useMutation({
    ...patchApiTablesMasterByIdMutation(),
    onMutate: async (variables) => {
      const { id } = variables.path
      const updateData = variables.body || {}

      // Save the previous tables state in case we need to roll back
      const previousTables = tables

      // Keep track of pending update for snapshot merging
      pendingUpdates.current.set(id, updateData)

      // Optimistically update the local state
      setTables((old) =>
        old.map((table) =>
          table.id === id ? { ...table, ...updateData } : table,
        ),
      )

      return { previousTables, id }
    },
    onSuccess: () => {
      toast.success('อัปเดตข้อมูลโต๊ะสำเร็จ')
    },
    onError: (err, variables, context) => {
      // Roll back to the previous state on error
      if (context?.previousTables) {
        setTables(context.previousTables)
      }
      const apiError = err as unknown as ApiError
      const errorMessage =
        apiError.body?.error || apiError.message || 'Unknown error'
      toast.error(`เกิดข้อผิดพลาดในการอัปเดต: ${errorMessage}`)
    },
  })

  const deleteMutation = useMutation({
    ...deleteApiTablesMasterByIdMutation(),
    onSuccess: () => {
      toast.success('ลบโต๊ะสำเร็จ')
    },
    onError: () => {
      toast.error('เกิดข้อผิดพลาดในการลบโต๊ะ')
    },
  })

  const importMutation = useMutation({
    ...postApiTablesMasterImportMutation(),
    onSuccess: (data) => {
      toast.success(`นำเข้าข้อมูลสำเร็จ ${data.count} รายการ`)
    },
    onError: () => {
      toast.error('เกิดข้อผิดพลาดในการนำเข้าข้อมูล')
    },
  })

  // Infinite scroll triggers (Simplified)
  const { ref: loadMoreRef } = useInView()

  // Process data
  const tableList = useMemo(() => {
    let filtered = tables
    if (debouncedQuery) {
      const lowerQuery = debouncedQuery.toLowerCase()
      filtered = tables.filter(
        (item) =>
          item.tableNumber?.toLowerCase().includes(lowerQuery) ||
          (item.notes && item.notes.toLowerCase().includes(lowerQuery)),
      )
    }

    const sortCol = sortBy || 'tableNumber'
    const isAsc = sortOrder !== 'desc'

    return [...filtered].sort((a, b) => {
      let comparison = 0
      if (sortCol === 'tableNumber') {
        const valA = String(a.tableNumber)
        const valB = String(b.tableNumber)
        const numA = Number(valA)
        const numB = Number(valB)
        const aIsNum = !isNaN(numA) && valA.trim() !== ''
        const bIsNum = !isNaN(numB) && valB.trim() !== ''

        if (aIsNum && bIsNum) {
          comparison = numA - numB
        } else if (aIsNum) {
          comparison = -1
        } else if (bIsNum) {
          comparison = 1
        } else {
          comparison = valA.localeCompare(valB, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }
      } else if (sortCol === 'capacity') {
        comparison = (a.capacity || 0) - (b.capacity || 0)
      } else if (sortCol === 'createdAt') {
        const getTime = (val: any) => {
          if (!val) return 0
          if (typeof val.toDate === 'function') return val.toDate().getTime()
          if (val.seconds) return val.seconds * 1000
          return new Date(val).getTime()
        }
        const timeA = getTime(a.createdAt)
        const timeB = getTime(b.createdAt)
        comparison = timeA - timeB
      }
      return isAsc ? comparison : -comparison
    })
  }, [tables, debouncedQuery, sortBy, sortOrder])

  const totalCount = tables.length

  const handleRefresh = async () => {
    // Handled by real-time updates
  }

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    tableList,
    isLoading,
    isFetchingNextPage: false,
    totalCount,
    loadMoreRef,
    handleRefresh,
    createMutation,
    updateMutation,
    deleteMutation,
    importMutation,
    tableOccupancyMap,
    tables,
  }
}
